import { useEffect, useState } from 'react'
import { Kopfleiste, Seitenkopf } from './Kopfleiste.js'
import { Fuss } from './Fuss.js'
import { useSeitenleben } from './bewegung.js'
import { RECHTSTEXTE, rechtstext, einsetzen, type Absatz, type Rechtstext } from '../recht.js'

/**
 * Die Rechtsseiten – AGB, Datenschutzerklärung, Auftragsbearbeitung, Impressum.
 *
 * Eine Seite für alle vier, weil sie sich nur im Inhalt unterscheiden und
 * nicht in der Form. Der Aufbau, von oben:
 *
 *   Kopfleiste (drei Glaspillen), Augenbraue «Rechtliches», Titel,
 *   Glas-Chip mit der Fassung, Vorspann. Dann eine Karte «Auf dieser Seite»
 *   mit zweispaltigem, nummeriertem Index. Dann EINE grosse Glaskarte mit
 *   allen Abschnitten: Nummer in einem kleinen Kästchen, Überschrift,
 *   Haarlinie, Text. Am Ende eine Zeile mit den übrigen Dokumenten und der
 *   grosse Fussbereich.
 *
 * Alle Abschnitte stehen in EINER Karte, nicht in je einer. Vierzehn Karten
 * untereinander sind vierzehn Kanten – das liest sich wie eine Liste von
 * Bruchstücken statt wie ein Dokument.
 *
 * Die Nummern stehen nicht in den Daten, sie kommen aus der Reihenfolge.
 * Stünden sie in `recht.ts`, müsste beim Einschieben eines Abschnitts alles
 * darunter von Hand umnummeriert werden – und beim zweiten Mal vergisst man
 * einen.
 */
export function Recht({
  id,
  angemeldet,
  aufStart,
  aufRecht,
  aufPfad,
  aufAnmelden,
  aufStarten
}: {
  id: string
  angemeldet: boolean
  aufStart: () => void
  aufRecht: (id: string) => void
  aufPfad: (pfad: string) => void
  aufAnmelden: () => void
  aufStarten: () => void
}): JSX.Element {
  const text = rechtstext(id) ?? RECHTSTEXTE[0]

  /* Beim Wechsel zwischen den Dokumenten nach oben – sonst beginnt man mitten drin. */
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [id])

  const leben = useSeitenleben<HTMLDivElement>()

  return (
    <div ref={leben} className="rp-glasseite">
      <Kopfleiste
        angemeldet={angemeldet}
        aufStart={aufStart}
        aufPfad={aufPfad}
        aufAnmelden={aufAnmelden}
        aufStarten={aufStarten}
      />

      <Seitenkopf
        augenbraue="Rechtliches"
        titel={text.kopf ?? text.titel}
        hell={text.kopfHell}
        meta={text.stand}
        vorspann={text.vorspann}
      />

      <main className="rp-glas-mitte">
        <nav className="rp-glas rp-glas-index" aria-label="Abschnitte">
          <p className="rp-augenbraue-punkt" style={{ justifyContent: 'flex-start' }}>
            Auf dieser Seite
          </p>
          <ol className="rp-glas-indexliste">
            {text.abschnitte.map((a, i) => (
              <li key={a.titel}>
                <a href={`#${anker(a.titel)}`}>
                  <span className="rp-glas-indexzahl">{zweistellig(i + 1)}</span>
                  <span>{a.titel}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="rp-glas">
          {text.abschnitte.map((a, i) => (
            <section className="rp-glas-abschnitt" key={a.titel} id={anker(a.titel)}>
              <h2 className="rp-glas-kopfzeile">
                <span className="rp-glas-zahl">{zweistellig(i + 1)}</span>
                {a.titel}
              </h2>
              {a.absaetze.map((p, j) => (
                <AbsatzStueck key={j} p={p} />
              ))}
            </section>
          ))}
        </article>

        {/*
          Die übrigen Dokumente als eine Zeile, nicht als Karten.

          Es sind drei Wörter. Drei Kacheln dafür wären dieselbe Information in
          dreifacher Fläche – und am Ende eines Rechtstextes will niemand noch
          eine Auswahl treffen, sondern nur wissen, wo das Nächste liegt.
        */}
        <div className="rp-glas-weitere">
          <p className="rp-glas-weiterelabel">Weitere rechtliche Dokumente</p>
          <nav aria-label="Weitere rechtliche Dokumente">
            {RECHTSTEXTE.filter((r) => r.id !== text.id).map((r) => (
              <button key={r.id} type="button" id={`weiter-${r.id}`} onClick={() => aufRecht(r.id)}>
                {r.titel}
              </button>
            ))}
          </nav>
        </div>
      </main>

      <Fuss aufPfad={aufPfad} />
    </div>
  )
}

/** 1 wird zu «01». Gleiche Breite für alle Nummern, auch ab der zehnten. */
function zweistellig(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/**
 * Aus «Verantwortliche Stelle» wird «verantwortliche-stelle».
 *
 * Der Index braucht ein Ziel, und das Ziel soll lesbar bleiben: wer den
 * Verweis kopiert, schickt «/agb#kuendigung» und nicht «/agb#abschnitt-7».
 * Umlaute werden aufgelöst – ein Anker mit «ü» funktioniert, sieht in der
 * Adresszeile aber als Prozentfolge aus, und genau die wird weitergegeben.
 */
function anker(titel: string): string {
  return titel
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/** «AGB» statt «Allgemeine Geschäftsbedingungen» – im Fuss zählt die Breite. */
function kurz(r: Rechtstext): string {
  if (r.id === 'agb') return 'AGB'
  if (r.id === 'datenschutz') return 'Datenschutz'
  if (r.id === 'avv') return 'Auftragsbearbeitung'
  return 'Impressum'
}

/**
 * Ein Absatz: Liste, hervorgehobene Zeile, E-Mail oder Fliesstext.
 *
 * Die Liste kennt einen Sonderfall: beginnt ein Punkt mit «Wort. » in
 * Fettschrift, steht davor ein Stichwort. Geschrieben wird es im Text als
 * «Wer wir sind. Raporto ist …», getrennt am ersten Punkt. Das spart ein
 * zweites Feld in den Daten für etwas, das eine Schreibkonvention ist.
 */
function AbsatzStueck({ p }: { p: Absatz }): JSX.Element {
  if (p.liste) {
    return (
      <ul>
        {p.liste.map((z, i) => (
          <li key={i}>
            <Punkt text={z} />
          </li>
        ))}
      </ul>
    )
  }
  const fertig = einsetzen(p.text ?? '')
  /* Eine Zeile, die nur aus einer Adresse besteht, wird anklickbar. */
  const mail = /^E-Mail:\s*(\S+@\S+)$/.exec(fertig)
  if (mail) {
    return (
      <p>
        E-Mail:{' '}
        <a className="rp-glas-mail" href={`mailto:${mail[1]}`}>
          {mail[1]}
        </a>
      </p>
    )
  }
  return (
    <p className={p.stark ? 'rp-glas-stark' : undefined} style={{ whiteSpace: 'pre-line' }}>
      {fertig}
    </p>
  )
}

function Punkt({ text }: { text: string }): JSX.Element {
  const fertig = einsetzen(text)
  const treffer = /^([^.]{2,34}\.)\s(.+)$/s.exec(fertig)
  if (!treffer) return <>{fertig}</>
  return (
    <>
      <strong className="rp-glas-stark">{treffer[1]}</strong> {treffer[2]}
    </>
  )
}

/**
 * Der Fussbereich der öffentlichen Seiten – eine Stelle, dieselben Verweise.
 *
 * Bleibt für die Seiten, die noch die schmale Fusszeile tragen.
 */
export function Rechtsfuss({ aufRecht }: { aufRecht: (id: string) => void }): JSX.Element {
  const [jahr] = useState(() => new Date().getFullYear())
  return (
    <>
      {RECHTSTEXTE.filter((r) => r.id !== 'avv').map((r) => (
        <button
          key={r.id}
          type="button"
          className="rp-textknopf"
          id={`fuss-${r.id}`}
          onClick={() => aufRecht(r.id)}
        >
          {kurz(r)}
        </button>
      ))}
      <span className="rp-fuss-jahr">© {jahr} Raporto</span>
    </>
  )
}
